/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Skeleton, Tag } from '../../components/console/ui';
import { useAuthStore } from '../../store/authStore';
import { securityService } from '../../services/api';
import toast from 'react-hot-toast';

interface LoginSession {
  id: string;
  ip_address: string;
  user_agent: string;
  device_type: string;
  browser: string;
  os: string;
  location: string;
  is_current: boolean;
  created_at: string;
  last_active_at: string;
}

interface SecurityInfo {
  auth_method: string;
  last_login: string;
  total_sessions: number;
}

const SecuritySettings: React.FC = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());

  // Fetch security info - gracefully handle if API not available
  const { data: securityInfo, isLoading: isLoadingInfo } = useQuery({
    queryKey: ['securityInfo'],
    queryFn: async () => {
      try {
        const response = await securityService.getInfo();
        return response.data as SecurityInfo;
      } catch {
        // Return default security info as fallback
        return {
          auth_method: 'email_otp',
          last_login: '',
          total_sessions: 1,
        } as SecurityInfo;
      }
    },
    retry: false,
  });

  // Fetch login sessions - gracefully handle if API not available
  const { data: sessions, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['loginSessions'],
    queryFn: async () => {
      try {
        const response = await securityService.getSessions();
        return response.data as LoginSession[];
      } catch {
        // Return current session as fallback
        return [{
          id: 'current',
          ip_address: '',
          user_agent: navigator.userAgent,
          device_type: 'desktop',
          browser: 'Unknown',
          os: 'Unknown',
          location: '',
          is_current: true,
          created_at: new Date().toISOString(),
          last_active_at: new Date().toISOString(),
        }] as LoginSession[];
      }
    },
    retry: false,
  });

  // Revoke single session mutation
  const revokeSessionMutation = useMutation({
    mutationFn: (sessionId: string) => securityService.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loginSessions'] });
      queryClient.invalidateQueries({ queryKey: ['securityInfo'] });
      toast.success('Session revoked successfully');
    },
    onError: () => {
      toast.error('Failed to revoke session');
    },
  });

  // Revoke all other sessions mutation
  const revokeAllMutation = useMutation({
    mutationFn: () => securityService.revokeAllOtherSessions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loginSessions'] });
      queryClient.invalidateQueries({ queryKey: ['securityInfo'] });
      setSelectedSessions(new Set());
      toast.success('All other sessions have been revoked');
    },
    onError: () => {
      toast.error('Failed to revoke sessions');
    },
  });

  // Revoke selected sessions mutation
  const revokeSelectedMutation = useMutation({
    mutationFn: async (sessionIds: string[]) => {
      await Promise.all(sessionIds.map(id => securityService.revokeSession(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loginSessions'] });
      queryClient.invalidateQueries({ queryKey: ['securityInfo'] });
      setSelectedSessions(new Set());
      toast.success('Selected sessions have been revoked');
    },
    onError: () => {
      toast.error('Failed to revoke selected sessions');
    },
  });

  const toggleSessionSelection = (sessionId: string) => {
    setSelectedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleRevokeSelected = () => {
    const sessionIds = Array.from(selectedSessions);
    if (sessionIds.length > 0) {
      revokeSelectedMutation.mutate(sessionIds);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateString);
  };

  const isLoading = isLoadingInfo || isLoadingSessions;

  if (isLoading) {
    return <Skeleton className="h-[360px] rounded-[14px]" />;
  }

  const otherSessions = sessions?.filter((s) => !s.is_current) || [];
  const rowCls = 'flex flex-wrap justify-between items-center gap-3 py-3.5 border-t border-ps-raised first:border-t-0';

  return (
    <>
      <section className="ps-card px-5 sm:px-6 py-2" aria-labelledby="signin-heading">
        <div className={rowCls}>
          <div className="min-w-0">
            <h2 id="signin-heading" className="m-0 text-base font-medium text-ps-text">
              Sign-in method
            </h2>
            <p className="m-0 mt-1 text-ps-muted text-sm">
              Passwordless. A one-time code is sent to <span className="text-ps-text">{user?.email}</span> each time you sign in.
              Codes expire after 10 minutes and work once.
            </p>
          </div>
          <Tag tone="ok">Email OTP · active</Tag>
        </div>
        {securityInfo?.last_login && (
          <div className={rowCls}>
            <div className="text-sm text-ps-muted">Last sign-in</div>
            <div className="text-sm text-ps-text">{formatDate(securityInfo.last_login)}</div>
          </div>
        )}
      </section>

      <section className="ps-card px-5 sm:px-6 py-2" aria-labelledby="sessions-heading">
        <div className={rowCls}>
          <h2 id="sessions-heading" className="m-0 text-base font-medium text-ps-text">
            Active sessions
          </h2>
          {otherSessions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedSessions.size > 0 && (
                <Button variant="danger" size="sm" onClick={handleRevokeSelected} disabled={revokeSelectedMutation.isPending}>
                  {revokeSelectedMutation.isPending ? 'Revoking…' : `Revoke selected (${selectedSessions.size})`}
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => revokeAllMutation.mutate()} disabled={revokeAllMutation.isPending}>
                {revokeAllMutation.isPending ? 'Signing out…' : 'Sign out other sessions'}
              </Button>
            </div>
          )}
        </div>

        {sessions?.map((session) => {
          const isSelected = selectedSessions.has(session.id);
          const title = [session.browser && session.browser !== 'Unknown' ? session.browser : null, session.os && session.os !== 'Unknown' ? session.os : null]
            .filter(Boolean)
            .join(' · ') || session.device_type || 'Unknown device';
          const meta = [
            session.location,
            session.ip_address,
            session.is_current
              ? 'active now'
              : getRelativeTime(session.last_active_at) === 'Just now'
                ? 'active just now'
                : `last active ${getRelativeTime(session.last_active_at)}`,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={session.id} className={`${rowCls} ${isSelected ? 'bg-ps-hover/50 -mx-2 px-2 rounded-md' : ''}`}>
              <div className="flex items-start gap-3 min-w-0">
                {!session.is_current && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSessionSelection(session.id)}
                    className="mt-1 w-[18px] h-[18px] accent-[#F97316] cursor-pointer"
                    aria-label={`Select session ${title}`}
                  />
                )}
                <div className="min-w-0">
                  <div className="font-medium text-ps-text">
                    {title}
                    {session.is_current && (
                      <Tag tone="ok" className="ml-2">
                        Current
                      </Tag>
                    )}
                  </div>
                  <div className="text-ps-subtle text-[13px] font-mono break-all">{meta}</div>
                </div>
              </div>
              {!session.is_current && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => revokeSessionMutation.mutate(session.id)}
                  disabled={revokeSessionMutation.isPending}
                >
                  Revoke
                </Button>
              )}
            </div>
          );
        })}

        {(!sessions || sessions.length === 0) && (
          <div className={`${rowCls} text-sm text-ps-muted`}>No active sessions found.</div>
        )}
      </section>
    </>
  );
};

export default SecuritySettings;
